import { useContext, useMemo } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { ShippingOption } from '@ortros/stoux-lib'
import { useQuery } from '@tanstack/react-query'

import { queries } from '@/core/queries'
import { CartMachineContext } from '@/modules/Cart/Cart.machine'
import { CartContext, CartContextValue } from '@/modules/Cart/Cart.vm'

type StoreSelection = {
    store_id: string
    shipping_option_selected: any
}

type FormValues = {
    stores: StoreSelection[]
}

export const useViewModel = () => {
    const {
        cartActor: [state, send],
    } = useContext(CartContext)

    const shippingOptionsQuery = useQuery({
        ...queries.cart.listShippingOptionsByCart(state.context.cart?.id!),
        enabled: !!state.context.cart?.id,
    })

    const shippingOptionsByStores = useMemo(
        () => shippingOptionsQuery.data ?? [],
        [shippingOptionsQuery.data],
    )

    const form = useForm<FormValues>()

    const { fields, append, update } = useFieldArray({
        control: form.control,
        name: 'stores',
    })

    type SelectShippingOptionsArgs = {
        store_id: string
        shipping_option_id: string
    }
    const onSelectShippingOption = ({
        store_id,
        shipping_option_id,
    }: SelectShippingOptionsArgs) => {
        const foundStore = fields.find((T) => {
            return T.store_id === store_id
        })

        if (!!foundStore) {
            const storeIndex = fields.findIndex(
                (store) => store.store_id === store_id,
            )

            update(storeIndex, {
                ...foundStore,
                shipping_option_selected: shipping_option_id,
            })
            return
        }

        append({
            store_id,
            shipping_option_selected: shipping_option_id,
        })
    }

    const onSubmit = () => {
        if (!hasSelectedForAllStores) {
            return
        }

        const shippingOptionsSelected = fields.map((field) => {
            const store = Object.values(shippingOptionsByStores).find(
                (store) => store.id === field.store_id,
            )
            const shippingOption = store?.shipping_options.find(
                (option) => option.id === field.shipping_option_selected,
            )

            return {
                store_id: field.store_id,
                shipping_option: shippingOption!,
            }
        })

        send({
            type: 'UPDATE_OPTIONS',
            updatedOptions: {
                ...state.context.options,
                shipping_methods:
                    shippingOptionsSelected as unknown as CartMachineContext['options']['shipping_methods'],
            },
        })

        send({ type: 'NEXT' })
    }

    const onRetry = () => {
        send({ type: 'RETRY' })
    }

    const hasSelectedForAllStores = useMemo(
        () =>
            Object.values(shippingOptionsByStores).every((store) => {
                const foundStore = fields.find((T) => T.store_id === store.id)
                return !!foundStore
            }) &&
            fields.length === Object.values(shippingOptionsByStores).length,
        [fields, shippingOptionsByStores],
    )

    return {
        fields,
        hasSelectedForAllStores,
        dataSource: shippingOptionsByStores,
        onSelectShippingOption,
        onSubmit,
        onRetry,
    }
}
